/**
 * №16.2.1 — the PLAN lens of the full process view (mockup 16.2.1-fullview-plan-v1).
 *
 * The ordered flat table grouped by stage: checkbox · glyph · Step · Assignee ·
 * Due · Status. Assignee and Due edit in place (popovers shared with Drive); the
 * work-state and the escapes live in the rail. Sortable columns, filter chips, a
 * live count line, and a bulk bar over the checkbox selection. Same selection
 * model as Drive — a clicked row drives the SAME properties rail.
 */
import { useMemo, useState } from "react";
import type { DetailSegment, DetailStep } from "../../hooks/use-workflow";
import { PropertiesRail, AssigneePopover, DuePopover } from "./WorkflowDriveLens";
import {
  resolveAssignee, fmtDate, relDays, isOverdue, stepIsDone, triggerText,
  type LensProps, type Selection,
} from "./wf-fullview-shared";

type SortKey = "step" | "assignee" | "due" | "status";
type Filter = "open" | "mine" | "agents" | "overdue" | "unassigned" | "done";

interface FlatRow { seg: DetailSegment; step: DetailStep; }

/** The step's coarse plan state, from its own status literal. */
function planState(st: DetailStep): "done" | "ready" | "waiting" | "draft" | "pending" {
  if (stepIsDone(st)) return "done";
  if (st.status === "ready") return st.assigneeKind === "agent" ? "draft" : "ready";
  if (st.status === "waiting") return "waiting";
  return "pending";
}

export function WorkflowPlanLens(props: LensProps) {
  const { data, actions, busy, selected, onSelect } = props;
  const segments = data.detail.segments;
  const dir = data.directory;

  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "step", dir: 1 });
  const [filters, setFilters] = useState<Set<Filter>>(new Set<Filter>(["open", "done"]));
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [editAssignee, setEditAssignee] = useState<number | null>(null);
  const [editDue, setEditDue] = useState<number | null>(null);

  function toggleFilter(f: Filter) {
    setFilters((prev) => { const n = new Set(prev); if (n.has(f)) n.delete(f); else n.add(f); return n; });
  }
  function toggleCheck(no: number) {
    setChecked((prev) => { const n = new Set(prev); if (n.has(no)) n.delete(no); else n.add(no); return n; });
  }

  const allRows = useMemo<FlatRow[]>(() => segments.flatMap((seg) => seg.steps.map((step) => ({ seg, step }))), [segments]);

  // "Mine" = steps whose resolved assignee is the current user. The projection's
  // person `assigneeCode` is a user id; we also fall back to email/name in case a
  // legacy row stored those. (TODO: confirm person-code semantics if this drifts.)
  function isMine(step: DetailStep): boolean {
    if (step.assigneeKind !== "person") return false;
    const uid = dir.currentUserId;
    if (!uid) return false;
    if (step.assigneeCode === uid) return true;
    const m = dir.members.find((mm) => mm.id === uid);
    return !!m && (step.assigneeCode === m.email || step.assigneeCode === m.name);
  }

  function rowPasses(r: FlatRow): boolean {
    const ps = planState(r.step);
    const active: Filter[] = [];
    if (filters.has("open")) active.push("open");
    if (filters.has("done")) active.push("done");
    if (filters.has("mine")) active.push("mine");
    if (filters.has("agents")) active.push("agents");
    if (filters.has("overdue")) active.push("overdue");
    if (filters.has("unassigned")) active.push("unassigned");
    if (active.length === 0) return true;
    // Positive-OR semantics across the chips (a row shows if it matches any active chip).
    return active.some((f) => {
      switch (f) {
        case "open": return ps === "ready" || ps === "waiting" || ps === "draft" || ps === "pending";
        case "done": return ps === "done";
        case "mine": return isMine(r.step);
        case "agents": return r.step.assigneeKind === "agent";
        case "overdue": return (ps === "ready" || ps === "waiting") && isOverdue(r.step.dueDate);
        case "unassigned": return !r.step.assigneeKind || r.step.assigneeKind === "unassigned";
        default: return false;
      }
    });
  }

  // Counts (over ALL rows — the honest tally, not the filtered subset).
  const counts = useMemo(() => {
    let done = 0, open = 0, waiting = 0, upcoming = 0;
    for (const r of allRows) {
      const ps = planState(r.step);
      if (ps === "done") done++;
      else if (ps === "ready" || ps === "draft") open++;
      else if (ps === "waiting") waiting++;
      else upcoming++;
    }
    return { done, open, waiting, upcoming };
  }, [allRows]);

  // Group rows by stage, preserving segment order; within a group, apply the sort.
  const groups = useMemo(() => {
    const passing = allRows.filter(rowPasses);
    const bySeg = new Map<number, FlatRow[]>();
    for (const r of passing) {
      const arr = bySeg.get(r.seg.segmentNo) ?? [];
      arr.push(r);
      bySeg.set(r.seg.segmentNo, arr);
    }
    const cmp = (a: FlatRow, b: FlatRow): number => {
      const s = sort.dir;
      switch (sort.key) {
        case "assignee": {
          const av = a.step.assigneeKind ? resolveAssignee(a.step.assigneeKind, a.step.assigneeCode, dir).who : "￿";
          const bv = b.step.assigneeKind ? resolveAssignee(b.step.assigneeKind, b.step.assigneeCode, dir).who : "￿";
          return av.localeCompare(bv) * s;
        }
        case "due": {
          const av = a.step.dueDate ?? "￿";
          const bv = b.step.dueDate ?? "￿";
          return av.localeCompare(bv) * s;
        }
        case "status": return planState(a.step).localeCompare(planState(b.step)) * s;
        default: return a.step.name.localeCompare(b.step.name) * s;
      }
    };
    return segments
      .filter((seg) => (bySeg.get(seg.segmentNo)?.length ?? 0) > 0)
      .map((seg) => ({ seg, rows: (bySeg.get(seg.segmentNo) ?? []).slice().sort(cmp) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, filters, sort, segments, dir]);

  function setSortKey(key: SortKey) {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }
  const arrow = (key: SortKey) => (sort.key === key ? <span className="ar">{sort.dir === 1 ? "↑" : "↓"}</span> : null);

  const checkedNos = [...checked];

  return (
    <div className="wfv-body">
      <div className="wfv-spine">
        <div className="wfv-tools">
          {(["open", "mine", "agents", "overdue", "unassigned", "done"] as Filter[]).map((f) => (
            <button key={f} className={`wfv-chip ${filters.has(f) ? "is-on" : ""}`} onClick={() => toggleFilter(f)}>
              {f === "done" ? "✓ Done" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <span className="right">{counts.done} done · {counts.open} open · {counts.waiting} waiting · {counts.upcoming} upcoming</span>
        </div>

        {checkedNos.length > 0 ? (
          <div className="wfv-bulk">
            <b>{checkedNos.length} selected</b>
            <span className="wfv-pop">
              <button className="btn btn--secondary btn--sm" disabled={busy} onClick={() => setEditAssignee(-1)}>Assign to…</button>
              {editAssignee === -1 ? (
                <AssigneePopover dir={dir} busy={busy} onClose={() => setEditAssignee(null)}
                  onPick={(kind, code) => { actions.bulkAssign(checkedNos, kind, code); setEditAssignee(null); }} />
              ) : null}
            </span>
            <span className="wfv-pop">
              <button className="btn btn--secondary btn--sm" disabled={busy} onClick={() => setEditDue(-1)}>Set due…</button>
              {editDue === -1 ? (
                <DuePopover ruleLabel="coming with date rules" currentDate={null} busy={busy} onClose={() => setEditDue(null)}
                  onPick={(date) => { actions.bulkSetDue(checkedNos, date); setEditDue(null); }} />
              ) : null}
            </span>
            <button className="btn btn--secondary btn--sm" disabled={busy} onClick={() => { actions.bulkMarkDone(checkedNos); setChecked(new Set()); }}>Mark done</button>
            <button className="clear" onClick={() => setChecked(new Set())}>clear</button>
          </div>
        ) : null}

        <div className="wfv-tablewrap">
          <table className="wfv-table">
            <thead>
              <tr>
                <th className="c" />
                <th style={{ width: "42%" }} onClick={() => setSortKey("step")}>Step {arrow("step")}</th>
                <th onClick={() => setSortKey("assignee")}>Assignee {arrow("assignee")}</th>
                <th onClick={() => setSortKey("due")}>Due {arrow("due")}</th>
                <th onClick={() => setSortKey("status")}>Status {arrow("status")}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(({ seg, rows }) => (
                <GroupBlock key={seg.segmentNo} seg={seg} rows={rows} segments={segments}
                  selected={selected} onSelect={onSelect} dir={dir} busy={busy} actions={actions}
                  checked={checked} toggleCheck={toggleCheck}
                  editAssignee={editAssignee} setEditAssignee={setEditAssignee}
                  editDue={editDue} setEditDue={setEditDue} />
              ))}
              {groups.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-3)", padding: "var(--space-5)" }}>No steps match these filters.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <PropertiesRail {...props} />
    </div>
  );
}

function GroupBlock({ seg, rows, segments, selected, onSelect, dir, busy, actions, checked, toggleCheck, editAssignee, setEditAssignee, editDue, setEditDue }: {
  seg: DetailSegment; rows: FlatRow[]; segments: DetailSegment[];
  selected: Selection | null; onSelect: (s: Selection | null) => void;
  dir: LensProps["data"]["directory"]; busy: boolean; actions: LensProps["actions"];
  checked: Set<number>; toggleCheck: (no: number) => void;
  editAssignee: number | null; setEditAssignee: (v: number | null) => void;
  editDue: number | null; setEditDue: (v: number | null) => void;
}) {
  const done = seg.state === "done" || seg.state === "skipped";
  const waiting = seg.state === "waiting";
  const header = done
    ? <><span className="tick">✓</span> {seg.name.toUpperCase()} <span className="cnt">{seg.stepsTotal} step{seg.stepsTotal === 1 ? "" : "s"} · {seg.state === "skipped" ? "skipped" : "done"}{seg.completedAt ? ` ${fmtDate(seg.completedAt)}` : ""}</span></>
    : waiting
      ? <>{seg.name.toUpperCase()} <span className="cnt">upcoming · opens when {triggerText(seg.trigger, segments)}</span></>
      : <>{seg.name.toUpperCase()} <span className="cnt">{seg.stepsDone} of {seg.stepsTotal}</span></>;

  return (
    <>
      <tr className={`wfv-grp ${done ? "is-doneg" : ""}`}>
        <td colSpan={5}>{header}{seg.stepsOrdered ? <span className="ord">▤ in order</span> : null}</td>
      </tr>
      {rows.map(({ step }) => (
        <PlanRow key={step.stepEntryNo} seg={seg} step={step} segments={segments}
          selected={selected} onSelect={onSelect} dir={dir} busy={busy} actions={actions}
          checked={checked.has(step.stepEntryNo)} toggleCheck={toggleCheck}
          editAssignee={editAssignee === step.stepEntryNo} setEditAssignee={setEditAssignee}
          editDue={editDue === step.stepEntryNo} setEditDue={setEditDue} />
      ))}
    </>
  );
}

function PlanRow({ seg, step, selected, onSelect, dir, busy, actions, checked, toggleCheck, editAssignee, setEditAssignee, editDue, setEditDue }: {
  seg: DetailSegment; step: DetailStep; segments: DetailSegment[];
  selected: Selection | null; onSelect: (s: Selection | null) => void;
  dir: LensProps["data"]["directory"]; busy: boolean; actions: LensProps["actions"];
  checked: boolean; toggleCheck: (no: number) => void;
  editAssignee: boolean; setEditAssignee: (v: number | null) => void;
  editDue: boolean; setEditDue: (v: number | null) => void;
}) {
  const ps = planState(step);
  const asg = step.assigneeKind ? resolveAssignee(step.assigneeKind, step.assigneeCode, dir) : null;
  const isSel = selected?.kind === "step" && selected.stepEntryNo === step.stepEntryNo;
  const overdue = (ps === "ready" || ps === "waiting") && isOverdue(step.dueDate);
  const rel = relDays(step.dueDate);
  const rowCls = ps === "done" ? "is-done" : ps === "ready" || ps === "draft" ? "is-ready" : ps === "waiting" ? "is-wait" : "is-pend";
  const glyph = ps === "done" ? "✓" : ps === "ready" || ps === "draft" ? "•" : "·";
  const ruleLabel = seg.trigger?.dateType ? `${seg.trigger.dateType} rule` : "coming with date rules";

  return (
    <tr className={`is-selectable ${rowCls} ${isSel ? "is-sel" : ""}`} onClick={() => onSelect({ kind: "step", segmentNo: seg.segmentNo, stepEntryNo: step.stepEntryNo })}>
      <td className="c" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={() => toggleCheck(step.stepEntryNo)} />
      </td>
      <td className="nm"><span className="stt">{glyph}</span> {step.name}</td>
      <td onClick={(e) => e.stopPropagation()}>
        <span className="wfv-pop">
          <span className={`wfv-who ${asg ? "" : "un"}`} onClick={() => setEditAssignee(editAssignee ? null : step.stepEntryNo)}>
            {asg ? <><span className={`wfv-avm ${asg.isAgent ? "is-agent" : ""}`}>{asg.isAgent ? "✦" : asg.initials}</span>{asg.who}</> : "Unassigned"}
          </span>
          {editAssignee ? (
            <AssigneePopover dir={dir} selectedKind={step.assigneeKind} selectedCode={step.assigneeCode} busy={busy}
              onClose={() => setEditAssignee(null)}
              onPick={(kind, code) => { actions.setAssignee(step.stepEntryNo, kind, code); setEditAssignee(null); }} />
          ) : null}
        </span>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <span className="wfv-pop">
          <span className={`wfv-due ${step.dueDate ? (overdue ? "od" : "") : "none"}`} onClick={() => setEditDue(editDue ? null : step.stepEntryNo)}>
            {step.dueDate ? <>{fmtDate(step.dueDate)}{rel ? <small>{rel}</small> : null}</> : "—"}
          </span>
          {editDue ? (
            <DuePopover ruleLabel={ruleLabel} currentDate={step.dueDate ?? null} busy={busy}
              onClose={() => setEditDue(null)}
              onPick={(date) => { actions.setDue(step.stepEntryNo, date); setEditDue(null); }} />
          ) : null}
        </span>
      </td>
      <td>{statusBadge(ps)}</td>
    </tr>
  );
}

function statusBadge(ps: ReturnType<typeof planState>): React.ReactNode {
  switch (ps) {
    case "ready": return <span className="wfv-badge b-ready">Ready</span>;
    case "draft": return <span className="wfv-badge b-draft">Draft ready</span>;
    case "waiting": return <span className="wfv-badge b-wait">Waiting</span>;
    case "done": return <span className="wfv-badge b-done">Done</span>;
    default: return null;
  }
}
